import React from 'react';
import {
  selectionsTotalLbs,
  type PackagingOption,
  type PackagingSelection,
} from '../utils/packagingMath';

export type CartUnit = 'LB' | 'KG';
export type { PackagingSelection };

export type CartLine =
  | {
      kind: 'product';
      productId: string;
      qty: number;
      unit: CartUnit;
      packagingSelections?: PackagingSelection[];
    }
  | { kind: 'package'; packageId: string; qty: number };

/** Legacy shape (v1 cart) — product-only. */
type LegacyCartLine = { productId: string; qty: number; unit: CartUnit };

export type CartLineInput = CartLine | LegacyCartLine;

type CartState = {
  lines: CartLine[];
};

type CartContextValue = {
  lines: CartLine[];
  addLine: (line: CartLineInput) => void;
  removeProduct: (productId: string) => void;
  removePackage: (packageId: string) => void;
  updateQty: (productId: string, qty: number) => void;
  updateProductPackaging: (
    productId: string,
    selections: PackagingSelection[],
    unit: CartUnit,
    options: PackagingOption[],
  ) => void;
  updatePackageQty: (packageId: string, qty: number) => void;
  clear: () => void;
};

const STORAGE_KEY = 'lewouhmart_cart_v3';
const LEGACY_V2_KEY = 'lewouhmart_cart_v2';
const LEGACY_STORAGE_KEY = 'lewouhmart_cart_v1';

const CartContext = React.createContext<CartContextValue | null>(null);

const PRODUCT_QTY_STEP = 0.5;
const PRODUCT_QTY_MIN = 0.5;

function snapToStep(qty: number, step: number) {
  if (!Number.isFinite(qty) || step <= 0) return 0;
  const units = Math.round(qty / step);
  return units * step;
}

function clampQty(qty: number) {
  if (!Number.isFinite(qty)) return PRODUCT_QTY_MIN;
  const stepped = snapToStep(qty, PRODUCT_QTY_STEP);
  return Math.max(PRODUCT_QTY_MIN, Math.round(stepped * 10) / 10);
}

function clampPackageQty(qty: number) {
  if (!Number.isFinite(qty)) return 1;
  return Math.max(1, Math.floor(qty));
}

function normalizePackagingSelections(raw: unknown): PackagingSelection[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const rows = raw
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const o = row as Record<string, unknown>;
      const optionId = typeof o.optionId === 'string' ? o.optionId : '';
      const count = Math.floor(Number(o.count));
      if (!optionId || count < 1) return null;
      return { optionId, count };
    })
    .filter(Boolean) as PackagingSelection[];
  return rows.length > 0 ? rows : undefined;
}

function normalizeAddLine(line: CartLineInput): CartLine {
  if ('kind' in line && line.kind === 'package') {
    return {
      kind: 'package',
      packageId: String(line.packageId),
      qty: clampPackageQty(line.qty),
    };
  }
  if ('kind' in line && line.kind === 'product') {
    const packagingSelections = normalizePackagingSelections(
      (line as { packagingSelections?: unknown }).packagingSelections,
    );
    return {
      kind: 'product',
      productId: String(line.productId),
      qty: clampQty(line.qty),
      unit: line.unit === 'KG' ? 'KG' : 'LB',
      packagingSelections,
    };
  }
  const l = line as LegacyCartLine;
  return {
    kind: 'product',
    productId: String(l.productId),
    qty: clampQty(l.qty),
    unit: l.unit === 'KG' ? 'KG' : 'LB',
  };
}

function normalizeLoadedLine(raw: unknown): CartLine | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.kind === 'package' && typeof o.packageId === 'string') {
    return {
      kind: 'package',
      packageId: o.packageId,
      qty: clampPackageQty(Number(o.qty)),
    };
  }
  if (typeof o.productId === 'string') {
    return {
      kind: 'product',
      productId: o.productId,
      qty: clampQty(Number(o.qty)),
      unit: o.unit === 'KG' ? 'KG' : 'LB',
      packagingSelections: normalizePackagingSelections(o.packagingSelections),
    };
  }
  return null;
}

function normalizeLoadedLines(lines: unknown[]): CartLine[] {
  return lines.map(normalizeLoadedLine).filter(Boolean) as CartLine[];
}

function loadInitialState(): CartState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { lines?: unknown };
      if (parsed?.lines && Array.isArray(parsed.lines)) {
        return { lines: normalizeLoadedLines(parsed.lines) };
      }
    }
  } catch {
    /* ignore */
  }
  try {
    const v2 = window.localStorage.getItem(LEGACY_V2_KEY);
    if (v2) {
      const parsed = JSON.parse(v2) as { lines?: unknown };
      if (parsed?.lines && Array.isArray(parsed.lines)) {
        return { lines: normalizeLoadedLines(parsed.lines) };
      }
    }
  } catch {
    /* ignore */
  }
  try {
    const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy) as { lines?: unknown };
      if (parsed?.lines && Array.isArray(parsed.lines)) {
        return { lines: normalizeLoadedLines(parsed.lines) };
      }
    }
  } catch {
    /* ignore */
  }
  return { lines: [] };
}

export function cartLineKey(line: CartLine): string {
  if (line.kind === 'product') {
    return `product:${line.productId}`;
  }
  return `package:${line.packageId}`;
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<CartState>(() => {
    if (typeof window === 'undefined') return { lines: [] };
    return loadInitialState();
  });

  React.useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore
    }
  }, [state]);

  const addLine = React.useCallback((line: CartLineInput) => {
    const next = normalizeAddLine(line);
    setState((prev) => {
      if (next.kind === 'package') {
        const pid = next.packageId;
        const existing = prev.lines.find(
          (l) => l.kind === 'package' && l.packageId === pid,
        );
        const qty = clampPackageQty(next.qty);
        if (!existing) {
          return { lines: [...prev.lines, { kind: 'package', packageId: pid, qty }] };
        }
        return {
          lines: prev.lines.map((l) =>
            l.kind === 'package' && l.packageId === pid
              ? { ...l, qty: clampPackageQty(l.qty + qty) }
              : l,
          ),
        };
      }
      const productId = String(next.productId);
      const existing = prev.lines.find(
        (l) => l.kind === 'product' && l.productId === productId,
      );
      const qty = clampQty(next.qty);
      if (!existing) {
        return { lines: [...prev.lines, { ...next, productId, qty }] };
      }
      return {
        lines: prev.lines.map((l) =>
          l.kind === 'product' && l.productId === productId
            ? { ...l, qty: clampQty(l.qty + qty), unit: next.unit }
            : l,
        ),
      };
    });
  }, []);

  const removeProduct = React.useCallback((productId: string) => {
    const id = String(productId);
    setState((prev) => ({
      lines: prev.lines.filter((l) => !(l.kind === 'product' && l.productId === id)),
    }));
  }, []);

  const removePackage = React.useCallback((packageId: string) => {
    const id = String(packageId);
    setState((prev) => ({
      lines: prev.lines.filter((l) => !(l.kind === 'package' && l.packageId === id)),
    }));
  }, []);

  const updateQty = React.useCallback((productId: string, qty: number) => {
    const id = String(productId);
    setState((prev) => ({
      lines: prev.lines.map((l) => {
        if (l.kind !== 'product' || l.productId !== id) return l;
        if (l.packagingSelections?.length) return l;
        return { ...l, qty: clampQty(qty) };
      }),
    }));
  }, []);

  const updateProductPackaging = React.useCallback(
    (
      productId: string,
      selections: PackagingSelection[],
      unit: CartUnit,
      options: PackagingOption[],
    ) => {
      const id = String(productId);
      const totalLbs = selectionsTotalLbs(selections, options);
      const qty =
        unit === 'KG'
          ? Math.round((totalLbs / 2.20462) * 100) / 100
          : Math.round(totalLbs * 10) / 10;
      setState((prev) => {
        const existing = prev.lines.find(
          (l) => l.kind === 'product' && l.productId === id,
        );
        if (!existing) {
          if (selections.length === 0 || qty <= 0) return prev;
          return {
            lines: [
              ...prev.lines,
              {
                kind: 'product' as const,
                productId: id,
                qty: Math.max(qty, 0.1),
                unit,
                packagingSelections: selections,
              },
            ],
          };
        }
        if (selections.length === 0) {
          return {
            lines: prev.lines.filter(
              (l) => !(l.kind === 'product' && l.productId === id),
            ),
          };
        }
        return {
          lines: prev.lines.map((l) =>
            l.kind === 'product' && l.productId === id
              ? {
                  ...l,
                  qty: Math.max(qty, 0.1),
                  unit,
                  packagingSelections: selections,
                }
              : l,
          ),
        };
      });
    },
    [],
  );

  const updatePackageQty = React.useCallback((packageId: string, qty: number) => {
    const id = String(packageId);
    setState((prev) => ({
      lines: prev.lines.map((l) =>
        l.kind === 'package' && l.packageId === id
          ? { ...l, qty: clampPackageQty(qty) }
          : l,
      ),
    }));
  }, []);

  const clear = React.useCallback(() => setState({ lines: [] }), []);

  const value = React.useMemo<CartContextValue>(
    () => ({
      lines: state.lines,
      addLine,
      removeProduct,
      removePackage,
      updateQty,
      updateProductPackaging,
      updatePackageQty,
      clear,
    }),
    [
      addLine,
      clear,
      removeProduct,
      removePackage,
      state.lines,
      updateQty,
      updateProductPackaging,
      updatePackageQty,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = React.useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
